import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";


/* =====================================================
   THUNKS
===================================================== */


/* ----------------- CREATE BOOKING ----------------- */
export const createBooking = createAsyncThunk(
    "booking/create",
    async ({ vendorId, payload }, { rejectWithValue }) => {
        try {

            console.log("vendorId booking", vendorId)

            const { data } = await api.post(
                `/booking/book-appointment/${vendorId}`,
                payload
            );
            return data.data;
        } catch (err) {
            console.log("CREATE BOOKING ERROR:", err.response?.data);
            return rejectWithValue(
                err.response?.data?.message || "Create booking failed"
            );
        }
    }
);


/* ----------------- CUSTOMER BOOKINGS ----------------- */
export const fetchCustomerBookings = createAsyncThunk(
    "booking/fetchCustomer",
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await api.get(
                "/booking/my-bookings"
            );
            return data.data;
        } catch (err) {
            console.log("CUSTOMER BOOKING ERROR:", err.response?.data);
            return rejectWithValue(
                err.response?.data?.message || "Fetch failed"
            );
        }
    }
);


/* ----------------- VENDOR BOOKINGS ----------------- */
export const fetchVendorBookings = createAsyncThunk(
    "booking/fetchVendor",
    async (vendorId, { rejectWithValue }) => {
        try {
            const res = await api.get(
                `/booking/vendor-bookings/${vendorId}`
            );

            // 🔥 IMPORTANT: backend usually sends { success, data }
            return res.data.data;
        } catch (err) {
            return rejectWithValue("Vendor booking api failed");
        }

    }
);

/* ----------------- VENDOR APPROVE / REJECT ----------------- */
export const vendorUpdateBooking = createAsyncThunk(
    "booking/vendorAction",
    async ({ bookingId, action }, { rejectWithValue }) => {
        try {
            const { data } = await api.put(
                `/booking/vendor/action/${bookingId}`,
                { action }
            );
            return data.data;
        } catch (err) {
            console.log("VENDOR ACTION ERROR:", err.response?.data);
            return rejectWithValue(
                err.response?.data?.message || "Action failed"
            );
        }
    }
);

/* ----------------- UPDATE BOOKING STATUS ----------------- */
export const updateBookingStatus = createAsyncThunk(
    "booking/updateStatus",
    async ({ bookingId, status }, { rejectWithValue }) => {
        try {
            const { data } = await api.put(
                `/booking/status/${bookingId}`,
                { status }
            );
            return data.data;
        } catch (err) {
            console.log("STATUS UPDATE ERROR:", err.response?.data);
            return rejectWithValue(
                err.response?.data?.message || "Update failed"
            );
        }
    }
);

/* ----------------- CUSTOMER CANCEL BOOKING ----------------- */
export const cancelBookingByCustomer = createAsyncThunk(
    "booking/cancel",
    async (bookingId, { rejectWithValue }) => {
        try {
            const { data } = await api.put(
                `/booking/cancel/${bookingId}`
            );
            return data.data;
        } catch (err) {
            console.log("CANCEL BOOKING ERROR:", err.response?.data);
            return rejectWithValue(
                err.response?.data?.message || "Cancel failed"
            );
        }
    }
);

/* ----------------- GET ALL BOOKINGS (ADMIN) ----------------- */
export const fetchAllBookings = createAsyncThunk(
    "booking/fetchAll",
    async (
        {
            vendorId,
            status,
            bookingId,
            page = 1,
            limit = 50,
        } = {},
        { rejectWithValue }
    ) => {
        try {
            const params = {};

            if (vendorId) params.vendorId = vendorId;
            if (status) params.status = status;
            if (bookingId) params.bookingId = bookingId;
            params.page = page;
            params.limit = limit;

            const { data } = await api.get("/booking/all-bookings", {
                params,
            });

            return data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message || "Fetch all bookings failed"
            );
        }
    }
);


/* =====================================================
   SLICE
===================================================== */

const bookingSlice = createSlice({
    name: "booking",
    initialState: {
        bookings: [],
        loading: false,
        error: null,
        success: false,
    },

    reducers: {
        clearBookingState: (state) => {
            state.loading = false;
            state.error = null;
            state.success = false;
        },
    },

    extraReducers: (builder) => {
        builder

            /* ================= CREATE ================= */
            .addCase(createBooking.pending, (state) => {
                state.loading = true;
            })
            .addCase(createBooking.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.bookings.unshift(action.payload);
            })
            .addCase(createBooking.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            /* ================= CUSTOMER BOOKINGS ================= */
            .addCase(fetchCustomerBookings.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchCustomerBookings.fulfilled, (state, action) => {
                state.loading = false;
                state.bookings = action.payload;
            })
            .addCase(fetchCustomerBookings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            /* ================= VENDOR BOOKINGS ================= */
            .addCase(fetchVendorBookings.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchVendorBookings.fulfilled, (state, action) => {
                state.loading = false;
                state.bookings = action.payload;
            })
            .addCase(fetchVendorBookings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            /* ================= VENDOR ACTION ================= */
            .addCase(vendorUpdateBooking.fulfilled, (state, action) => {
                const index = state.bookings.findIndex(
                    (b) => b._id === action.payload._id
                );
                if (index !== -1) {
                    state.bookings[index] = action.payload;
                }
            })

            /* ================= STATUS UPDATE ================= */
            .addCase(updateBookingStatus.fulfilled, (state, action) => {
                const index = state.bookings.findIndex(
                    (b) => b._id === action.payload._id
                );
                if (index !== -1) {
                    state.bookings[index] = action.payload;
                }
            })

            /* ================= CANCEL ================= */
            .addCase(cancelBookingByCustomer.fulfilled, (state, action) => {
                const index = state.bookings.findIndex(
                    (b) => b._id === action.payload._id
                );
                if (index !== -1) {
                    state.bookings[index] = action.payload;
                }
            })

            /* ADMIN – ALL BOOKINGS */
            .addCase(fetchAllBookings.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchAllBookings.fulfilled, (state, action) => {
                state.loading = false;
                state.bookings = action.payload.data;
                state.meta = action.payload.meta;
            })
            .addCase(fetchAllBookings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

    },
});

export const { clearBookingState } = bookingSlice.actions;
export default bookingSlice.reducer;
